// Contratos (schemas/DTOs) da fatia "intelligence": inteligência de vendas para moda.
// Regras puras e textos fixos em ../intelligence.ts. Fórmulas para a lojista em docs/INTELIGENCIA.md.
import { z } from 'zod';
import {
  COVERAGE_STATUSES,
  DEFAULT_LEAD_TIME_DAYS,
  DEFAULT_TARGET_DAYS,
  INTELLIGENCE_PERIODS,
  type CoverageStatus,
  type IneligibleReason,
  type InsightTone,
  type IntelligencePeriod,
  type ProductClassKey,
  type RankingReason,
  type TrendDirection,
} from '../intelligence';
import type { PublicProductDTO } from '../types';

// ============================================================================
// Consultas (query strings)
// ============================================================================

const periodDays = (def: IntelligencePeriod) =>
  z.coerce
    .number()
    .int()
    .refine((v) => (INTELLIGENCE_PERIODS as readonly number[]).includes(v), { message: 'Escolha 30, 60 ou 90 dias' })
    .default(def);

/** GET /intelligence/matrix */
export const intelligenceMatrixQuery = z.object({ days: periodDays(30) });
export type IntelligenceMatrixQuery = z.infer<typeof intelligenceMatrixQuery>;

/** GET /intelligence/ranking */
export const intelligenceRankingQuery = z.object({
  days: periodDays(30),
  limit: z.coerce.number().int().min(1).max(500).default(200),
});
export type IntelligenceRankingQuery = z.infer<typeof intelligenceRankingQuery>;

/** GET /intelligence/affinities */
export const intelligenceAffinitiesQuery = z.object({
  days: periodDays(90),
  limit: z.coerce.number().int().min(1).max(30).default(30),
});
export type IntelligenceAffinitiesQuery = z.infer<typeof intelligenceAffinitiesQuery>;

/** GET /intelligence/stock */
export const intelligenceStockQuery = z.object({
  /** Janela para a venda média (dias). */
  days: z.coerce.number().int().min(7).max(180).default(30),
  /** Prazo de entrega do fornecedor (dias). */
  leadTimeDays: z.coerce.number().int().min(0).max(120).default(DEFAULT_LEAD_TIME_DAYS),
  /** Dias de venda que a compra deve cobrir depois de chegar. */
  targetDays: z.coerce.number().int().min(1).max(365).default(DEFAULT_TARGET_DAYS),
  status: z.enum(COVERAGE_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(300),
});
export type IntelligenceStockQuery = z.infer<typeof intelligenceStockQuery>;

/** GET /intelligence/grid (sem productId nem categoryId = loja inteira). */
export const intelligenceGridQuery = z
  .object({
    productId: z.string().uuid('Produto inválido').optional(),
    categoryId: z.string().uuid('Categoria inválida').optional(),
    days: z.coerce.number().int().min(7).max(365).default(90),
    /** Peças por pacote na sugestão de compra. */
    packSize: z.coerce.number().int().min(1).max(500).default(12),
  })
  .refine((q) => !(q.productId && q.categoryId), { message: 'Escolha um produto ou uma categoria, não os dois', path: ['categoryId'] });
export type IntelligenceGridQuery = z.infer<typeof intelligenceGridQuery>;

// ============================================================================
// DTOs
// ============================================================================

/** De onde veio o resultado: foto noturna (< 24 h) ou cálculo na hora. */
export type InsightSource = 'snapshot' | 'live';

interface PeriodInfo {
  days: number;
  /** Primeiro e último dia (AAAA-MM-DD, fuso da loja, inclusivos). */
  from: string;
  to: string;
  /** Quando os números foram calculados (ISO). */
  generatedAt: string;
  source: InsightSource;
}

/** Métricas de um produto no período (base da matriz e do ranking). */
export interface ProductPerformanceDTO {
  productId: string;
  name: string;
  categoryId: string | null;
  categoryName: string | null;
  imageUrl: string | null;
  isActive: boolean;
  isFeatured: boolean;
  /** Pedidos distintos com o produto. */
  orders: number;
  /** Peças vendidas (já descontadas as devoluções). */
  quantity: number;
  /** Receita líquida de descontos (desconto do pedido rateado). */
  revenueCents: number;
  /** Preço médio praticado = receita / peças. */
  avgPriceCents: number;
  /** Custo das peças vendidas. Null: custo desconhecido OU usuário sem "Ver custo e lucro". */
  costCents: number | null;
  /** Margem de contribuição em R$ (receita − encargos − custo). Mesma regra de visibilidade do custo. */
  profitCents: number | null;
  /** Margem de contribuição em % da receita. Mesma regra de visibilidade do custo. */
  marginPercent: number | null;
  /** Participação na quantidade vendida pelos produtos elegíveis (%). Null quando não elegível. */
  sharePercent: number | null;
  class: ProductClassKey | null;
  /** Por que ficou sem classe ("aguardando dados"). */
  reason: IneligibleReason | null;
  /** Texto fixo da classe (PRODUCT_CLASS_META). */
  recommendation: string | null;
  previous: { orders: number; quantity: number; revenueCents: number };
  /** Variação da quantidade contra o período anterior (%). Null sem venda antes. */
  trendPercent: number | null;
  trend: TrendDirection;
}

/** GET /intelligence/matrix?days=30|60|90 */
export interface ProductMatrixDTO extends PeriodInfo {
  minOrders: number;
  /** Encargos sobre a venda aplicados na margem (settings.pricing: imposto + cartão + comissão + outros). */
  chargesPercent: number;
  /** false quando o usuário não tem "Ver custo e lucro" (custo, lucro e margem vêm null). */
  costVisible: boolean;
  cutoffs: { eligibleCount: number; popularityPercent: number | null; marginPercent: number | null };
  counts: Record<ProductClassKey, number> & { waiting: number };
  /** Produtos classificados, por classe (Estrela, Carro-chefe, Joia escondida, Encalhe) e participação. */
  items: ProductPerformanceDTO[];
  /** Vendidos no período, mas sem classe: menos de 5 pedidos ou sem custo. */
  waiting: ProductPerformanceDTO[];
}

export interface ProfitRankingItemDTO extends Omit<ProductPerformanceDTO, 'reason'> {
  /** 0..100 entre os elegíveis; null com `reason`. */
  score: number | null;
  reason: RankingReason | null;
}

/** GET /intelligence/ranking?days=30|60|90&limit=200 */
export interface ProfitRankingDTO extends PeriodInfo {
  minOrders: number;
  chargesPercent: number;
  costVisible: boolean;
  /** Ordenado por score (nulos no fim), depois receita. Inclui produtos ativos sem venda (reason 'sem_venda'). */
  items: ProfitRankingItemDTO[];
}

export interface AffinityProductDTO {
  productId: string;
  name: string;
  imageUrl: string | null;
  categoryName: string | null;
}

export interface AffinityPairDTO {
  a: AffinityProductDTO;
  b: AffinityProductDTO;
  /** Pedidos com A e B juntos (>= 2). */
  orders: number;
  /** Pedidos com A / com B. */
  ordersA: number;
  ordersB: number;
  /** Pedidos com o par / total de pedidos (%). */
  supportPercent: number;
  /** P(B|A) em %: de quem leva A, quantos levam B. */
  confidenceAB: number;
  /** P(A|B) em %. */
  confidenceBA: number;
  /** > 1: compram juntos mais do que o acaso explicaria. */
  lift: number;
}

/** GET /intelligence/affinities?days=90 */
export interface AffinitiesDTO extends PeriodInfo {
  /** Pedidos concluídos do período com pelo menos um produto cadastrado. */
  totalOrders: number;
  minPairOrders: number;
  /** Até 30 pares, mais frequentes primeiro (desempate pelo lift). */
  items: AffinityPairDTO[];
}

export interface StockCoverageItemDTO {
  productId: string;
  variantId: string | null;
  name: string;
  variantName: string | null;
  /** "Legging Flare Preta M" */
  label: string;
  options: Record<string, string>;
  sku: string | null;
  imageUrl: string | null;
  stock: number;
  minStock: number | null;
  /** Vendido na janela. */
  soldQty: number;
  /** Dias com a peça disponível na janela (dias esgotada ficam fora da média). */
  availableDays: number;
  /** Venda média por dia (2 casas). */
  dailyAvg: number;
  /** Estoque / venda média (1 casa). Null sem venda. */
  coverageDays: number | null;
  /** Dia estimado em que acaba (AAAA-MM-DD). */
  stockoutDate: string | null;
  status: CoverageStatus;
  /** Já pedido ao fornecedor e ainda não recebido. */
  onOrderQty: number;
  /** Compra sugerida para cobrir prazo + alvo. */
  suggestedQty: number;
  /** Custo unitário (null sem custo ou sem permissão de ver custo). */
  unitCostCents: number | null;
  lastSaleAt: string | null;
}

export interface IdleStockItemDTO {
  productId: string;
  variantId: string | null;
  name: string;
  variantName: string | null;
  label: string;
  imageUrl: string | null;
  stock: number;
  /** Estoque × custo (null sem custo ou sem permissão). */
  costCents: number | null;
  /** Estoque × preço de venda. */
  valueCents: number;
  lastSaleAt: string | null;
  /** Null = nunca vendeu. */
  daysWithoutSale: number | null;
}

/** GET /intelligence/stock?days=30&leadTimeDays=7&targetDays=30 */
export interface StockCoverageDTO extends PeriodInfo {
  leadTimeDays: number;
  targetDays: number;
  costVisible: boolean;
  counts: Record<CoverageStatus, number>;
  /** Ordenado por urgência (ruptura, crítico, atenção, ok, excesso). Itens esgotados sem venda na janela ficam fora. */
  items: StockCoverageItemDTO[];
  /** Capital parado: peças com estoque e sem venda há 60 dias (cadastradas há mais de 60 dias). */
  idle: {
    days: number;
    units: number;
    /** Soma de estoque × custo (null sem permissão). Peças sem custo ficam fora da soma. */
    costCents: number | null;
    valueCents: number;
    unitsWithoutCost: number;
    items: IdleStockItemDTO[];
  };
}

export interface GridValueDTO {
  value: string;
  soldQty: number;
  stock: number;
  /** Participação nas vendas (%, inteiros que somam 100). */
  sharePercent: number;
  /** Vendido / (vendido + estoque) (%). Null quando nenhum produto do recorte controla estoque. */
  sellThroughPercent: number | null;
}

/** GET /intelligence/grid?productId=|categoryId=&days=90&packSize=12 */
export interface SizeGridDTO {
  scope: { type: 'product' | 'category' | 'store'; id: string | null; name: string };
  days: number;
  from: string;
  to: string;
  generatedAt: string;
  totalSold: number;
  /** Menos de 10 peças vendidas: a proporção ainda é frágil. */
  lowData: boolean;
  /** Uma entrada por opção da grade (Tamanho, Cor...). Tamanhos em ordem PP, P, M, G, GG; demais por venda. */
  options: { name: string; kind: 'size' | 'color' | 'other'; values: GridValueDTO[] }[];
  /** Sugestão de compra por tamanho (null sem tamanho ou sem venda). */
  suggestion: {
    optionName: string;
    packSize: number;
    lines: { value: string; percent: number; qty: number }[];
    text: string;
  } | null;
  /** Cor × Tamanho (quando a grade tem as duas opções). */
  crossTable: {
    colorOption: string;
    sizeOption: string;
    colors: string[];
    sizes: string[];
    cells: { color: string; size: string; soldQty: number; stock: number }[];
  } | null;
}

export type IntelligenceAlertSeverity = 'danger' | 'warning' | 'info';

export interface IntelligenceAlertDTO {
  /** Chave estável: orders_awaiting, charges_expiring, payables_overdue, payables_due_today, stockout_selling, below_minimum, products_without_cost, catalog_without_photo, setup_pending. */
  id: string;
  severity: IntelligenceAlertSeverity;
  title: string;
  body: string;
  count: number;
  /** Rota do painel onde o problema se resolve. */
  link: string;
  /** Só no setup_pending: cada pendência com o seu link. */
  items?: { id: string; title: string; link: string }[];
}

/** GET /intelligence/alerts (cache de ~60 s por loja; só alertas com count > 0 e que o usuário pode resolver). */
export interface IntelligenceAlertsDTO {
  generatedAt: string;
  items: IntelligenceAlertDTO[];
}

export type IntelligenceHighlightKind = 'stockout' | 'star' | 'idle_capital' | 'hidden_gem' | 'trend' | 'pair';

export interface IntelligenceHighlightDTO {
  id: string;
  kind: IntelligenceHighlightKind;
  tone: InsightTone;
  /** Frase curta pronta (números em pt-BR, sem travessão). */
  text: string;
  link: string;
}

/** GET /intelligence/highlights (até 3). */
export interface IntelligenceHighlightsDTO {
  generatedAt: string;
  items: IntelligenceHighlightDTO[];
  /** true quando o plano não tem "Inteligência de vendas" (só destaques de estoque aparecem). */
  locked: boolean;
}

export const AI_RECOMMENDATION_AREAS = ['estoque', 'vendas', 'precos', 'vitrine', 'compras', 'financeiro'] as const;
export type AiRecommendationArea = (typeof AI_RECOMMENDATION_AREAS)[number];
export const AI_RECOMMENDATION_AREA_LABELS: Record<AiRecommendationArea, string> = {
  estoque: 'Estoque',
  vendas: 'Vendas',
  precos: 'Preços',
  vitrine: 'Vitrine e catálogo',
  compras: 'Compras',
  financeiro: 'Financeiro',
};

export interface AiRecommendationDTO {
  title: string;
  /** Por quê, citando os números. */
  why: string;
  /** O que fazer. */
  action: string;
  /** 1 = urgente (perde venda ou dinheiro agora), 2 = importante, 3 = melhoria. */
  priority: 1 | 2 | 3;
  area: AiRecommendationArea;
}

export type AiSummaryUnavailableReason = 'not_configured' | 'disabled';

/** GET e POST /intelligence/ai-summary */
export interface AiSummaryDTO {
  /** false: IA não configurada na instalação ou desligada pelo suporte. Os demais números continuam funcionando. */
  available: boolean;
  reason: AiSummaryUnavailableReason | null;
  message: string | null;
  /** Último resumo gerado (null se nunca gerou). */
  summary: {
    id: string;
    generatedAt: string;
    model: string | null;
    periodDays: number;
    recommendations: AiRecommendationDTO[];
  } | null;
  creditsUsed: number;
  creditsLimit: number | null;
  creditsRemaining: number | null;
}

/** POST /intelligence/refresh */
export interface IntelligenceRefreshDTO {
  refreshedAt: string;
  kinds: string[];
}

/** GET /public/stores/:slug/products/:productId/related ("Complete o look"). */
export interface RelatedProductsDTO {
  items: (PublicProductDTO & { relation: 'bought_together' | 'same_category' | 'best_seller' })[];
}
